import { ChecklistAnswerSpec, ChecklistQuestionSpec, QuestionType } from "@nova/schemas"
import { v4 } from "uuid"

export interface QuestionControls {
    editQuestion: <T extends QuestionType>(id: string, spec: ChecklistQuestionSpec<T>) => void
    deleteQuestion: (id: string) => void
    moveQuestion: (id: string, params: { direction: 1 | -1; animate?: boolean }) => void
    getNumQuestions: () => number
}

export type QuestionProps<T extends QuestionType = QuestionType> = {
    spec: ChecklistQuestionSpec<T>
    questionControls: QuestionControls
    autoFocus?: boolean
}

export const emptyQuestionSpec = <T extends QuestionType>(type: T): ChecklistQuestionSpec<T> => {
    const emptySpecMap: Record<QuestionType, ChecklistQuestionSpec> = {
        radio: {
            id: v4(),
            isRequired: false,
            type: "radio",
            question: "",
            options: ["Option 1"],
        },
        "multi-select": {
            id: v4(),
            isRequired: false,
            type: "multi-select",
            question: "",
            options: ["Option 1"],
        },
        text: {
            id: v4(),
            isRequired: false,
            type: "text",
            question: "",
            variant: "short",
        },
        "user-select": {
            id: v4(),
            type: "user-select",
            isRequired: false,
            question: "",
        },
        "aircraft-select": {
            id: v4(),
            type: "aircraft-select",
            isRequired: false,
            question: "",
        },
        header: {
            id: v4(),
            type: "header",
            question: "",
        },
    }

    return emptySpecMap[type] as ChecklistQuestionSpec<T>
}

export const emptyAnswerSpec = (question: ChecklistQuestionSpec): ChecklistAnswerSpec => {
    switch (question.type) {
        case "text":
            return {
                answer: "",
                questionId: question.id,
                type: "text",
            } satisfies ChecklistAnswerSpec<"text">
        case "multi-select":
            return {
                answer: question.options.map(() => false),
                questionId: question.id,
                type: "multi-select",
            } satisfies ChecklistAnswerSpec<"multi-select">
        case "radio":
            return {
                answer: question.options.map(() => false),
                questionId: question.id,
                type: "radio",
            } satisfies ChecklistAnswerSpec<"radio">
        case "user-select":
            return {
                answer: "",
                questionId: question.id,
                type: "user-select",
            } satisfies ChecklistAnswerSpec<"user-select">
        case "aircraft-select":
            return {
                answer: "",
                questionId: question.id,
                type: "aircraft-select",
            } satisfies ChecklistAnswerSpec<"aircraft-select">
        case "header":
            return {
                questionId: question.id,
                type: "header",
            } satisfies ChecklistAnswerSpec<"header">
    }
}
